import React, { useState, useCallback, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomAgGrid from "./CustomAgGrid";
import { exportToCSV } from "../services/csvExportService";
import { httpService } from "../services/httpService";
import { API_BASE_URL, REQ_ENC_KEY } from "../services/api";
import { encryptResponse } from "../services";
import OtpInput from "./common/OtpInput";

interface CustomAgGridDailogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  rowData: any[];
  columnDefs: any[];
  loading?: boolean;
  height?: string | number;
  width?: string | number;
  defaultColDef?: any;
  onGridReady?: (params: any) => void;
  onCellClicked?: (params: any) => void;
  onRowSelected?: (params: any) => void;
  rowSelection?: "single" | "multiple";
  theme?: any;
  gridOptions?: any;
  containerStyle?: React.CSSProperties;
  onExport?: () => void;
}

const CustomAgGridDailogBox: React.FC<CustomAgGridDailogProps> = ({
  open,
  onClose,
  title,
  rowData,
  columnDefs,
  loading,
  height,
  width,
  defaultColDef,
  onGridReady,
  onCellClicked,
  onRowSelected,
  rowSelection,
  theme,
  gridOptions,
  containerStyle,
  onExport,
}) => {
  const gridRef = useRef<any>(null);
  const [firstRow, setFirstRow] = useState(1);
  const [lastRow, setLastRow] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // Email OTP (MFA) verification before exporting data.
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isOtpPopupOpen, setIsOtpPopupOpen] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const updateRowCounts = useCallback(() => {
    if (gridRef.current?.api) {
      const api = gridRef.current.api;
      const displayedRowCount = api.getDisplayedRowCount();
      setTotalRows(displayedRowCount);
      if (displayedRowCount > 0) {
        const renderedNodes: any[] = [];
        api.forEachNodeAfterFilterAndSort((node: any, index: number) => {
          renderedNodes.push({ node, index });
        });

        if (renderedNodes.length > 0) {
          const firstRenderedIndex = api.getFirstDisplayedRowIndex();
          const lastRenderedIndex = api.getLastDisplayedRowIndex();

          setFirstRow(firstRenderedIndex + 1);
          setLastRow(lastRenderedIndex + 1);
        } else {
          setFirstRow(0);
          setLastRow(0);
        }
      } else {
        setFirstRow(0);
        setLastRow(0);
      }
    }
  }, []);

  const handleGridReady = useCallback((params: any) => {
    gridRef.current = params;
    updateRowCounts();
    if (onGridReady) {
      onGridReady(params);
    }
  }, [onGridReady, updateRowCounts]);

  // Runs the actual export once the email OTP has been verified.
  const performExport = useCallback(() => {
    if (onExport) {
      onExport();
      return;
    }

    const dataToExport: any[] = [];
    selectedRows.forEach(row => dataToExport.push(row));

    if (dataToExport.length === 0) {
      console.warn('No data to export');
      return;
    }
    const filename = title.toLowerCase().replace(/\s+/g, '-') + '.csv';
    exportToCSV({
      data: dataToExport,
      filename,
      includeTimestamp: true,
    });
  }, [onExport, title, selectedRows]);

  const handleOtpPopupClose = useCallback(() => {
    setIsOtpPopupOpen(false);
    setOtp("");
    setOtpError("");
  }, []);

  // Step 1: send an OTP to the logged-in user's email before exporting.
  const handleExport = useCallback(async () => {
    // Skip OTP when there's nothing to export via the default CSV path.
    if (!onExport && selectedRows.length === 0) {
      console.warn('No data to export');
      return;
    }

    const emailId = userDetails?.email_id || '';
    if (!emailId) {
      toast.error('Unable to find your email for verification.', { position: 'top-right', autoClose: 1500 });
      return;
    }

    setIsSendingOtp(true);
    setOtp("");
    setOtpError("");

    const payload = {
      request_id: 'export_data_otp',
      action: 'send_otp',
      data: await encryptResponse({
        email_id: emailId,
        purpose: 'mfa'
      }, REQ_ENC_KEY)
    };

    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/sendOTP`, payload);
      if (response.data.status === 200) {
        setIsOtpPopupOpen(true);
        toast.success('OTP sent successfully!', { position: 'top-right', autoClose: 1500 });
      } else {
        const message = response.data?.error?.errorDetails || 'Failed to send OTP. Please try again.';
        toast.error(message, { position: 'top-right', autoClose: 1500 });
      }
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while sending OTP.', { position: 'top-right', autoClose: 1500 });
    } finally {
      setIsSendingOtp(false);
    }
  }, [onExport, selectedRows, userDetails]);

  // Step 2: verify the OTP, then run the actual export.
  const verifyOtp = useCallback(async () => {
    if (otp.length !== 6) {
      setOtpError('Please enter the 6-digit OTP.');
      return;
    }

    const emailId = userDetails?.email_id || '';
    const payload = {
      request_id: 'export_data_verify_otp',
      action: 'verify_otp',
      data: await encryptResponse({
        email_id: emailId,
        otp: otp
      }, REQ_ENC_KEY)
    };

    setIsVerifyingOtp(true);
    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/verifyOTP`, payload);
      if (response.data.status === 200 && response.data.data) {
        setOtpError('');
        setIsOtpPopupOpen(false);
        setOtp("");
        performExport();
      } else {
        setOtpError('Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.log(error);
      setOtpError('An error occurred while verifying OTP.');
    } finally {
      setIsVerifyingOtp(false);
    }
  }, [otp, userDetails, performExport]);

  const handleClearFilter = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setFilterModel(null);
      updateRowCounts();
    }
  }, [updateRowCounts]);

  if (!open) return null;

  return (
    <>
      {/* Background Overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1300,
          backdropFilter: 'blur(4px)',
        }}
        // onClick={onClose}
      />
      
      {/* Dialog Content */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: '1400px',
          height: '80vh',
          zIndex: 1301,
          backgroundColor: "#ffffff",
          borderRadius: '12px',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.3)',
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          ...containerStyle,
        }}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          padding: 1,
          borderBottom: "none",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
          color: '#ffffff',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 600,
            letterSpacing: 0.5,
            textShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {title}
        </Typography>
        <IconButton
          onClick={() => {
            onClose();
            setSelectedRows([]);
          }}
          size="small"
          sx={{
            position: "absolute",
            right: 16,
            color: '#ffffff',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'rotate(90deg)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Export Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          padding: "4px 10px",
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltOffIcon fontSize="small" />}
          onClick={handleClearFilter}
          sx={{
            borderColor: "#667eea",
            color: "#667eea",
            textTransform: "none",
            fontWeight: 500,
            '&:hover': {
              borderColor: "#764ba2",
              backgroundColor: "rgba(102, 126, 234, 0.05)",
            },
          }}
        >
          Clear Filters
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<FileDownloadIcon fontSize="small" />}
          disabled={isSendingOtp || isVerifyingOtp || selectedRows.length === 0}
          onClick={handleExport}
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#ffffff",
            textTransform: "none",
            fontWeight: 500,
            '&:hover': {
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
            },
          }}
        >
          Export to CSV
        </Button>
      </Box>

      {/* Grid Content */}
      <Box sx={{ flex: 1, padding: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1 }}>
          <CustomAgGrid
            rowData={rowData}
            columnDefs={columnDefs}
            loading={loading}
            height={height || "100%"}
            width={width || "100%"}
            onGridReady={handleGridReady}
            onCellClicked={onCellClicked}
            // onRowSelected={onRowSelected}
            onRowSelected={(params) => {
              onRowSelected && onRowSelected(params);
              setSelectedRows(params.api.getSelectedRows());
            }}
            rowSelection={rowSelection}
            theme={theme}
            gridOptions={{
              ...gridOptions,
              onBodyScroll: updateRowCounts,
              onModelUpdated: updateRowCounts,
            }}
            defaultColDef={defaultColDef}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "8px 16px",
            borderTop: "1px solid #e0e0e0",
            marginTop: "8px",
            minHeight: "40px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
            color: '#ffffff',
            borderRadius: '0 0 8px 8px',
          }}
        >
          <Typography variant="body2" sx={{ color: "#ffffff", fontWeight: 500 }}>
            {totalRows > 0
              ? `Showing ${firstRow}-${lastRow} of ${totalRows}`
              : "No rows to display"}
          </Typography>
        </Box>
      </Box>
    </Box>

      {/* Send/Verify OTP Loader */}
      <Backdrop
        sx={{ color: '#fff', zIndex: 1400, backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        open={isSendingOtp || isVerifyingOtp}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {isVerifyingOtp ? 'Verifying OTP...' : 'Sending OTP...'}
          </Typography>
        </Box>
      </Backdrop>

      {/* Email OTP Verification Dialog */}
      <Dialog
        open={isOtpPopupOpen}
        onClose={handleOtpPopupClose}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1402 }}
        slotProps={{ paper: { sx: { width: '500px', maxWidth: '90vw' } } }}
      >
        <DialogTitle>Verify Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Enter the 6-digit OTP sent to <strong>{userDetails?.email_id}</strong> to export this data.
          </Typography>
          <OtpInput
            id="export-otp"
            value={otp}
            onChange={(value) => {
              setOtp(value);
              if (otpError) {
                setOtpError('');
              }
            }}
            error={!!otpError}
            helperText={otpError}
            autoFocus={isOtpPopupOpen}
            disabled={isVerifyingOtp}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOtpPopupClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={verifyOtp} color="primary" disabled={isVerifyingOtp || otp.length !== 6}>
            Verify OTP
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomAgGridDailogBox;
